import React from "react";

import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import { Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { Link, useHistory } from "react-router-dom";
import { BenAdminLeadUpdatableProps } from "RegisterBenAdmin/types";
import {
  composeValidators,
  validateEmail,
  validateHundred,
  validateNumber,
  validatePhone,
  validateZero,
  validateRequired,
  validateRequiredSignature,
} from "Utils/Validations";

import { useGetApplicationQuery, useUpdateApplicationMutation, useUploadedFilesQuery, useUploadFilesMutation } from "../api";
import Chrome from "../components/Chrome";
import Layout from "../components/Layout";
import LoadingPage from "../components/LoadingPage";
import { paymentURL, doneURL } from "../urls";
import BodyText from "Cobalt/BodyText";
import Button, { LinkButton } from "Cobalt/Button";
import { CardHeader, CardSection } from "Cobalt/Card";
import { H4 } from "Cobalt/Header";
import AppearingElement from "Common/AppearingElement";
import { SIFinalFormMultiFileInput, SIFinalFormField, SIFormRadioGroup, SIFormTextArea, SIFormSelect } from "Common/forms";
import { phoneMask } from "Common/forms/masks";
import { Row, Col } from "Common/Grid";
import { Div, Ul } from "Common/HTML";
import Icon from "Common/Icon";
import SignatureForm from "Common/Signature/SignatureForm";

const focusOnErrors = createDecorator();

interface SubmitProps extends BenAdminLeadUpdatableProps {
  files: File[];
  employeeHealthAccountFiles: File[];
}
const AdditionalInformationPage = () => {
  const { data: { benAdminLead } = {}, isLoading: isLoadingBenAdminLead } = useGetApplicationQuery();
  const id = benAdminLead?.externalID;
  const maskedProviderAccountNumber = benAdminLead?.maskedProviderAccountNumber;
  const maskedFundingAccountNumber = benAdminLead?.maskedFundingAccountNumber;
  const maskedFundingRoutingNumber = benAdminLead?.maskedFundingRoutingNumber;
  const history = useHistory();
  const [updateApplication] = useUpdateApplicationMutation();
  const { data: { files: uploadedFiles } = {} } = useUploadedFilesQuery({ storageKey: "insurance" }, { skip: !id });
  const [uploadFiles] = useUploadFilesMutation();
  const { data: { files: uploadedEmployeeHealthAccountFiles } = {} } = useUploadedFilesQuery({ storageKey: "health_account" }, { skip: !id });

  const submitHandler = async ({ files, employeeHealthAccountFiles, ...values }: SubmitProps) => {
    if (files?.length > 0) {
      const uploadInsuranceFilesRes = await uploadFiles({ files, storageKey: "insurance" });
      if (uploadInsuranceFilesRes.error) {
        const { errors } = uploadInsuranceFilesRes.error.data;
        return errors;
      }
    }

    if (employeeHealthAccountFiles?.length > 0) {
      const uploadEmployeeHealthAccountFilesRes = await uploadFiles({ files: employeeHealthAccountFiles, storageKey: "health_account" });
      if (uploadEmployeeHealthAccountFilesRes.error) {
        const { errors } = uploadEmployeeHealthAccountFilesRes.error.data;
        return errors;
      }
    }

    const res = await updateApplication(values);
    if (res.error) {
      const { errors } = res.error.data;
      return errors;
    }

    history.push(doneURL);
    return null;
  };

  return isLoadingBenAdminLead ? (
    <LoadingPage />
  ) : (
    <Chrome>
      <Layout>
        <Form
          onSubmit={submitHandler}
          decorators={[focusOnErrors]}
          initialValues={{
            ...benAdminLead,
            contributions: benAdminLead.contributions?.length > 0 ? benAdminLead.contributions : [{}],
          }}
          mutators={{
            ...arrayMutators,
          }}
        >
          {({ handleSubmit, values }) => {
            const { contributionType, accountType, provider } = values;
            const healthAccountName = ["hsa", "fsa"].includes(accountType) ? accountType.toUpperCase() : "Health Account";
            return (
              <form onSubmit={handleSubmit}>
                <CardSection>
                  <Link component={LinkButton} size="medium" to={paymentURL} color="link" pl={0} mb={3}>
                    <Icon icon="chevron-left" pr={3} />
                    Back
                  </Link>
                  <H4>Benefits Information</H4>
                  <BodyText mt={4}>Enter benefit plans and broker information to sync payroll deductions to NetSuite.</BodyText>
                </CardSection>
                <CardHeader>1. Company Information</CardHeader>
                <CardSection>
                  <Row mb={4}>
                    <Col>
                      <BodyText>Let us know how we can get in touch with you.</BodyText>
                    </Col>
                  </Row>
                  <Row>
                    <SIFinalFormField sm={6} name="companyName" label="Company Name" />
                  </Row>
                  <Row>
                    <SIFinalFormField sm={6} name="contactName" label="Contact Name" />
                  </Row>
                  <Row>
                    <SIFinalFormField
                      sm={6}
                      name="contactPhoneNumber"
                      label="Phone Number"
                      type="text"
                      componentProps={{ placeholder: "(555) 555-5555", mask: phoneMask }}
                      validate={composeValidators(validatePhone)}
                    />
                  </Row>
                </CardSection>
                <CardHeader>2. Benefit Invoices</CardHeader>
                <CardSection>
                  <Row>
                    <Col sm={8}>
                      <BodyText>Upload the most recent invoice for any of the following benefits:</BodyText>
                      <BodyText size="small">
                        <Ul pl={4} my={3}>
                          <li>Medical</li>
                          <li>Dental</li>
                          <li>Vision</li>
                          <li>Life</li>
                          <li>Disability</li>
                          <li>AD&D</li>
                        </Ul>
                      </BodyText>
                    </Col>
                    {uploadedFiles?.length && uploadedFiles.length > 0 ? (
                      <Col sm={4}>
                        <Div>
                          <BodyText weight="bold">Previously Uploaded Invoices:</BodyText>
                          <BodyText size="small" my={3}>
                            {uploadedFiles.map((file, index) => (
                              <div className="text-ellipsis" key={`${file}_${index}`}>
                                {file}
                              </div>
                            ))}
                          </BodyText>
                        </Div>
                      </Col>
                    ) : null}
                    <SIFinalFormField name="files" component={SIFinalFormMultiFileInput} />
                  </Row>
                </CardSection>
                <CardHeader>3. Benefits Broker Information</CardHeader>
                <CardSection>
                  <Row mb={4}>
                    <Col>
                      <BodyText>We will automatically notify your broker when you make updates to benefits.</BodyText>
                    </Col>
                  </Row>
                  <Row>
                    <SIFinalFormField sm={6} name="brokerName" label="Name" />
                  </Row>
                  <Row>
                    <SIFinalFormField sm={6} name="brokerEmail" label="Email" validate={composeValidators(validateEmail)} />
                  </Row>
                  <Row>
                    <SIFinalFormField
                      sm={6}
                      name="brokerPhone"
                      label="Phone Number"
                      type="text"
                      componentProps={{ placeholder: "(555) 555-5555", mask: phoneMask }}
                      validate={composeValidators(validatePhone)}
                    />
                  </Row>
                </CardSection>
                <CardHeader>4. Contributions</CardHeader>
                <CardSection>
                  <Row mb={4}>
                    <Col>
                      <BodyText>Enter the amount that you currently contribute to your employees benefits</BodyText>
                    </Col>
                  </Row>
                  <Row>
                    <SIFinalFormField
                      name="contributionType"
                      component={SIFormRadioGroup}
                      componentProps={{
                        options: {
                          percent_of_base_plan: "% of the least expensive plan",
                          percentage: "% of total premium",
                          fixed: "Flat dollar amount",
                        },
                      }}
                    />
                  </Row>
                  <FieldArray name="contributions">
                    {({ fields }) => (
                      <>
                        {fields.map((member, index) => (
                          <React.Fragment key={`${contributionType}_${index}`}>
                            <AppearingElement show={contributionType !== "fixed"}>
                              <Row>
                                <SIFinalFormField
                                  sm={6}
                                  name={`${member}.employeePercentage`}
                                  label="Percentage for Employees"
                                  componentProps={{
                                    appendContent: "%",
                                    type: "number",
                                    min: 0,
                                    max: 100,
                                  }}
                                  guidance={["Typically ", <strong key="employee-percentage">{">50%"}</strong>, " of the employee's monthly cost."]}
                                  validate={composeValidators(validateZero, validateHundred, validateNumber)}
                                />
                              </Row>
                              <Row>
                                <SIFinalFormField
                                  sm={6}
                                  name={`${member}.dependentPercentage`}
                                  label="Percentage for Dependents"
                                  componentProps={{
                                    appendContent: "%",
                                    type: "number",
                                    min: 0,
                                    max: 100,
                                  }}
                                  guidance={[
                                    "Typically ",
                                    <strong key="dependent-percentage">0%</strong>,
                                    " of the dependent cost. Many companies do not reimburse for dependents.",
                                  ]}
                                  validate={composeValidators(validateZero, validateHundred, validateNumber)}
                                />
                              </Row>
                            </AppearingElement>
                            <AppearingElement show={contributionType === "fixed"}>
                              <Row>
                                <SIFinalFormField
                                  sm={6}
                                  name={`${member}.employeeFixedAmount`}
                                  label="Amount for Employees"
                                  componentProps={{
                                    prependContent: "$",
                                    type: "number",
                                    min: 0,
                                  }}
                                  guidance={["Typically ", <strong key="employee-amount">half or more</strong>, " of the employee's monthly cost."]}
                                  validate={composeValidators(validateZero, validateNumber)}
                                />
                              </Row>
                              <Row>
                                <SIFinalFormField
                                  sm={6}
                                  name={`${member}.dependentFixedAmount`}
                                  label="Amount for Dependents"
                                  componentProps={{
                                    prependContent: "$",
                                    type: "number",
                                    min: 0,
                                  }}
                                  guidance={["Typically ", <strong key="dependent-amount">zero</strong>, ". Many companies do not reimburse for dependents."]}
                                  validate={composeValidators(validateZero, validateNumber)}
                                />
                              </Row>
                            </AppearingElement>
                            <Row>
                              <SIFinalFormField
                                name="otherContributionInformation"
                                label="Additional Information on Company Benefit Contributions"
                                component={SIFormTextArea}
                                componentProps={{
                                  placeholder:
                                    "Please include any additional details about custom benefit contributions, unique company contribution rules, or any other relevant information we should be aware of.",
                                }}
                              />
                            </Row>
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </FieldArray>
                </CardSection>
                <CardHeader>5. Coverage Preferences</CardHeader>
                <CardSection>
                  <Row>
                    <SIFinalFormField
                      sm={6}
                      name="probationaryPeriod"
                      label="Waiting Period For New Employees"
                      component={SIFormSelect}
                      componentProps={{
                        options: {
                          "1st of month after hire date": "1st of month after hire date",
                          "1 month": "1 month",
                          "2 months": "2 months",
                        },
                      }}
                      guidance={[
                        "Most companies choose ",
                        <strong key="waiting-period">'1st of month after hire date'</strong>,
                        ". This is the amount of time an employee needs to work before being eligible for insurance benefits.",
                      ]}
                      validate={validateRequired}
                    />
                  </Row>
                </CardSection>
                <CardHeader>6. Health Accounts</CardHeader>
                <CardSection>
                  <Row>
                    <SIFinalFormField
                      sm={10}
                      name="accountType"
                      label="Do you contribute to employee Health Accounts? (HSA/FSA)"
                      component={SIFormRadioGroup}
                      componentProps={{
                        options: {
                          none: "No",
                          hsa: "Yes, we contribute to HSA (Health Savings Account)",
                          fsa: "Yes, we contribute to FSA (Flexible Spending Account)",
                        },
                      }}
                    />
                  </Row>
                  <AppearingElement show={accountType !== "none"}>
                    <Row>
                      <SIFinalFormField
                        name="yearlyContributionAmount"
                        label="How much do you contribute to employee Health Accounts per year?"
                        componentProps={{
                          prependContent: "$",
                          type: "number",
                          min: 0,
                        }}
                        sm={6}
                        validate={composeValidators(validateZero, validateNumber)}
                      />
                    </Row>
                    <Row>
                      <SIFinalFormField
                        name="contributionFrequency"
                        label="How do you split up the annual amount over the year?"
                        component={SIFormSelect}
                        componentProps={{
                          placeholder: "--------",
                          options: {
                            Annually: "annually",
                            Quarterly: "quarterly",
                            Monthly: "monthly",
                            "Each Pay Period": "pay_period",
                          },
                        }}
                        sm={6}
                      />
                    </Row>
                    <Row>
                      <SIFinalFormField
                        name="provider"
                        label={`${healthAccountName} Provider`}
                        component={SIFormSelect}
                        componentProps={{
                          placeholder: "--------",
                          options: {
                            Fidelity: "fidelity",
                            HealthEquity: "health_equity",
                            "HSA Bank": "hsa_bank",
                            Lively: "lively",
                            Optum: "optum",
                            "Bank of America": "bank_of_america",
                            Other: "other",
                          },
                        }}
                        sm={6}
                      />
                    </Row>
                    <AppearingElement show={provider === "other"}>
                      <Row>
                        <SIFinalFormField name="providerOther" label={`${healthAccountName} Provider Name`} sm={6} />
                      </Row>
                    </AppearingElement>
                    <Row>
                      <SIFinalFormField
                        name="providerAccountNumber"
                        label={`${healthAccountName} Provider Group Account Number`}
                        sm={6}
                        componentProps={{ ...(maskedProviderAccountNumber ? { placeholder: maskedProviderAccountNumber } : {}) }}
                      />
                    </Row>
                  </AppearingElement>
                </CardSection>
                <AppearingElement show={accountType !== "none"}>
                  <CardHeader className="border-t-1 border-0 border-solid border-t-gray-300">7. Funding Bank Account for your {healthAccountName}</CardHeader>
                  <CardSection>
                    <Row>
                      <SIFinalFormField name="fundingBank" label="Bank Name" sm={6} />
                    </Row>
                    <Row>
                      <SIFinalFormField
                        name="fundingAccountNumber"
                        label="Account Number"
                        sm={6}
                        componentProps={{ ...(maskedFundingAccountNumber ? { placeholder: maskedFundingAccountNumber } : {}) }}
                      />
                    </Row>
                    <Row>
                      <SIFinalFormField
                        name="fundingRoutingNumber"
                        label="Routing Number"
                        sm={6}
                        componentProps={{ ...(maskedFundingRoutingNumber ? { placeholder: maskedFundingRoutingNumber } : {}) }}
                      />
                    </Row>
                  </CardSection>
                  <CardHeader>8. Employee Health Account Information</CardHeader>
                  <CardSection>
                    <Row>
                      <Col sm={8}>
                        <BodyText>Please provide information on your employees&lsquo; {healthAccountName}s. Upload a file containing:</BodyText>
                        <BodyText size="small">
                          <Ul pl={4} my={3}>
                            <li>Employee Names</li>
                            <li>Employee {healthAccountName} Numbers</li>
                            <li>Additional contribution made by employees (if any)</li>
                          </Ul>
                        </BodyText>
                      </Col>
                      {uploadedEmployeeHealthAccountFiles?.length && uploadedEmployeeHealthAccountFiles.length > 0 ? (
                        <Col sm={4}>
                          <Div>
                            <BodyText weight="bold">Previously Uploaded Employee Health Account Information:</BodyText>
                            <BodyText size="small" my={3}>
                              {uploadedEmployeeHealthAccountFiles.map((file, index) => (
                                <div className="text-ellipsis" key={`${file}_${index}`}>
                                  {file}
                                </div>
                              ))}
                            </BodyText>
                          </Div>
                        </Col>
                      ) : null}
                      <SIFinalFormField name="employeeHealthAccountFiles" component={SIFinalFormMultiFileInput} />
                    </Row>
                  </CardSection>
                </AppearingElement>
                <CardHeader>Signature</CardHeader>
                <CardSection>
                  <Row>
                    <SIFinalFormField
                      lg={10}
                      name="signature"
                      label="Sign below to authorize SimplyInsured to send the information you have provided to the insurance company."
                      component={SignatureForm}
                      componentProps={{
                        showSubmitButton: false,
                        clearButtonText: "Clear Signature",
                        responsive: true,
                      }}
                      validate={validateRequiredSignature}
                      errorRight
                    />
                  </Row>
                </CardSection>
                <CardSection className="flex border-t-1 border-0 border-solid border-t-gray-300">
                  <Button ml="auto" type="submit" size="medium">
                    Next
                  </Button>
                </CardSection>
              </form>
            );
          }}
        </Form>
      </Layout>
    </Chrome>
  );
};

export default AdditionalInformationPage;
